// Domain types for the Purchases module

export type PurchaseOrderStatus = 'draft' | 'sent' | 'partial' | 'received' | 'cancelled' | 'closed';
export type PurchaseOrderOrigin = 'order' | 'direct';
export type PurchaseReceptionStatus = 'active' | 'voided';
export type PurchaseReturnStatus = 'active' | 'voided';

export type PurchaseOrderRow = {
  id: string;
  branchId: string;
  supplierId: string | null;
  supplierName: string | null;
  status: PurchaseOrderStatus;
  origin: PurchaseOrderOrigin;
  orderNumber: string | null;
  total: number;
  notes: string | null;
  expectedDelivery: string | null;
  supplierInvoiceRef: string | null;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
  receivedAt: string | null;
  receivedBy: string | null;
  voidedAt: string | null;
  voidedBy: string | null;
  voidReason: string | null;
  closedAt: string | null;
  closedBy: string | null;
  closeReason: string | null;
  /** Agregados de ítems (join con purchase_order_items) para la fila de lista. */
  itemsCount?: number;
  orderedUnits?: number;
  receivedUnits?: number;
};

export type PurchaseOrderItemRow = {
  id: string;
  purchaseOrderId: string;
  productId: string | null;
  productName: string | null;
  productSku: string | null;
  quantity: number;
  unitCost: number | null;
  unitOfMeasure: string | null;
  receivedQuantity: number;
  targetBranchId?: string | null;
  /** #694: variante de destino del ítem (null = stock del producto padre). */
  variantId?: string | null;
};

export type PurchaseReceptionRow = {
  id: string;
  branchId: string;
  purchaseOrderId: string | null;
  supplierId: string | null;
  supplierName: string | null;
  status: PurchaseReceptionStatus;
  receivedAt: string;
  receivedBy: string | null;
  notes: string | null;
  supplierInvoiceRef: string | null;
  voidedAt: string | null;
  voidedBy: string | null;
  voidReason: string | null;
  createdAt: string;
};

export type PurchaseReceptionItemRow = {
  id: string;
  receptionId: string;
  purchaseOrderItemId: string | null;
  productId: string | null;
  productName: string | null;
  productSku: string | null;
  quantity: number;
  unitCost: number | null;
  unitOfMeasure: string | null;
  stockLotId: string | null;
  stockMovementId: string | null;
  targetBranchId?: string | null;
  returnedQuantity?: number;
};

export type PurchaseReturnRow = {
  id: string;
  branchId: string;
  receptionId: string | null;
  supplierId: string | null;
  supplierName: string | null;
  status: PurchaseReturnStatus;
  returnedAt: string;
  returnedBy: string | null;
  reason: string | null;
  notes: string | null;
  voidedAt: string | null;
  voidedBy: string | null;
  voidReason: string | null;
  createdAt: string;
};

export type PurchaseReturnItemRow = {
  id: string;
  returnId: string;
  receptionItemId: string | null;
  productId: string | null;
  productName: string | null;
  productSku: string | null;
  quantity: number;
  unitCost: number | null;
  unitOfMeasure: string | null;
  stockMovementId: string | null;
};

export type PurchaseOrderWithItems = PurchaseOrderRow & { items: PurchaseOrderItemRow[] };
export type PurchaseReceptionWithItems = PurchaseReceptionRow & { items: PurchaseReceptionItemRow[] };

export type PurchaseListQuery = {
  branchId: string;
  search?: string;
  status?: PurchaseOrderStatus | 'all';
  supplierId?: string | 'all';
  origin?: PurchaseOrderOrigin | 'all';
  /** Atajo de cola (paso 4). `to_receive` filtra sent|partial; pay/overdue usan `queueOrderIds`. */
  queue?: 'all' | 'to_receive' | 'to_pay' | 'overdue';
  queueOrderIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
  sortBy?: 'date' | 'total' | 'supplier';
  sortDir?: 'asc' | 'desc';
};

export type PurchasesSummary = {
  totalOrders: number;
  totalAmount: number;
  /** Por recibir: sent | partial. */
  pendingReception: number;
  partialReception: number;
  received: number;
  cancelled: number;
  toPay: number;
  /** Saldo pendiente (amount - amountPaid) de gastos ligados sin pagar. */
  toPayAmount: number;
  overdue: number;
};

export const PURCHASES_LIST_PAGE_SIZE = 50;
